import { useEffect, useState } from "react";
import { api, fmtBRL, fmtCompetencia, fmtDate, fmtDateTime, extractError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { CheckCircle2, AlertOctagon, QrCode, Copy, BookOpen, Zap, Printer, CreditCard } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { useLojistaAuth } from "@/context/AuthContext";
import BoletoFebraban from "@/components/BoletoFebraban";
import FloatingWhatsAppButton from "@/components/FloatingWhatsAppButton";
import { printBoletoWithPageSize, fetchBeneficiario, prepararPix, mesclarPix } from "@/lib/boletoPrint";
import ComprovantePagamento from "@/components/ComprovantePagamento";
import { CardPayment, initMercadoPago } from "@mercadopago/sdk-react";

let _mpInitialized = false;
async function ensureMpInitialized() {
  if (_mpInitialized) return true;
  try {
    const { data } = await api.get("/config/mp-public");
    if (data?.public_key) {
      initMercadoPago(data.public_key, { locale: "pt-BR" });
      _mpInitialized = true;
      return true;
    }
  } catch {
    return false;
  }
  return false;
}


export default function LojistaDashboard() {
  const { lojista } = useLojistaAuth();
  const [boleto, setBoleto] = useState(null);
  const [historico, setHistorico] = useState([]);
  const [checkoutModal, setCheckoutModal] = useState(false);
  const [pagamento, setPagamento] = useState(null);
  const [metodo, setMetodo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [beneficiario, setBeneficiario] = useState(null);
  const [pixReady, setPixReady] = useState(null); // cobrança BolePix do Inter p/ impressão
  const [comprovante, setComprovante] = useState(null); // fatura PAGA a imprimir
  const [cardModal, setCardModal] = useState(false);
  const [cardResult, setCardResult] = useState(null); // resposta do pagamento com cartão (Mercado Pago)
  const [mpReady, setMpReady] = useState(false);

  const abrirCartao = async () => {
    if (!boleto) return;
    const ok = await ensureMpInitialized();
    if (!ok) {
      toast.error("Não foi possível carregar o Mercado Pago. Tente novamente.");
      return;
    }
    setMpReady(true);
    setCardResult(null);
    setCardModal(true);
  };

  const onCardSubmit = async (formData) => {
    // formData vem do Card Payment Brick com {token, payment_method_id,
    // installments, issuer_id, payer{email, identification{type,number}}}
    try {
      const { data } = await api.post("/pagamentos/lojista/cartao", {
        boleto_id: boleto.id,
        token: formData.token,
        payment_method_id: formData.payment_method_id,
        installments: formData.installments,
        issuer_id: formData.issuer_id,
        payer_email: formData?.payer?.email,
        identification_type: formData?.payer?.identification?.type,
        identification_number: formData?.payer?.identification?.number,
      });
      setCardResult(data);
      if (data.status === "approved" && data.boleto_baixado) {
        toast.success("Pagamento aprovado! Sua fatura foi baixada.");
        await load();
      } else if (data.status === "in_process" || data.status === "pending") {
        toast.info("Pagamento em análise. Aguarde a confirmação por WhatsApp.");
      } else {
        toast.warning(`Pagamento com status ${data.status}. Detalhe: ${data.status_detail || "-"}`);
      }
    } catch (e) {
      toast.error(extractError(e) || "Falha no pagamento com cartão");
      throw e; // permite Brick reabilitar o botão para nova tentativa
    }
  };


  const [outrosAbertos, setOutrosAbertos] = useState([]); // parcelas de acordo, boletos extras

  const load = async () => {
    const [abertosResp, h] = await Promise.all([
      api.get("/pagamentos/boletos/abertos"),
      api.get("/pagamentos/boletos/historico"),
    ]);
    const abertos = abertosResp.data || [];
    // Boleto principal = o mais antigo vencido, ou o próximo a vencer
    const principal = abertos.length > 0 ? abertos[0] : null;
    setBoleto(principal);
    setOutrosAbertos(abertos.slice(1));
    setHistorico(h.data);
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { setPixReady(null); }, [boleto?.id]);

  const iniciarCheckout = async (metodoSelecionado, recorrente = false) => {
    if (!boleto) return;
    setLoading(true);
    try {
      const { data } = await api.post("/pagamentos/checkout", {
        boleto_id: boleto.id,
        metodo: metodoSelecionado,
        recorrente,
      });
      setPagamento(data);
      setMetodo(metodoSelecionado);
      setCheckoutModal(true);
    } catch (e) {
      toast.error(extractError(e) || "Não foi possível gerar o Pix agora. Tente novamente.");
    } finally { setLoading(false); }
  };

  const imprimirComprovante = async (fatura) => {
    const benef = beneficiario || await fetchBeneficiario();
    if (benef && !beneficiario) setBeneficiario(benef);
    setComprovante(fatura);
    setTimeout(() => printBoletoWithPageSize(), 200);
  };

  const copy = (txt) => {
    navigator.clipboard.writeText(txt);
    toast.success("Copiado!");
  };

  const antecipar = async () => {
    if (!window.confirm("Gerar cobrança antecipada do aluguel base para o mês corrente?")) return;
    try {
      const { data } = await api.post("/pagamentos/antecipar");
      toast.success(`Cobrança antecipada gerada · ${fmtBRL(data.valor_total)}`);
      await load();
    } catch (e) {
      toast.error(extractError(e) || "Falha ao gerar cobrança antecipada");
    }
  };

  const temPendencia = boleto && boleto.status !== "PAGO";

  return (
    <div className="space-y-6 fade-up">
      {/* Status Card */}
      {temPendencia ? (
        <Card
          data-testid="loj-status-pendente"
          className="p-6 rounded-2xl border-destructive/40 bg-destructive/5 text-destructive"
        >
          <div className="flex items-start gap-3 mb-4">
            <AlertOctagon size={22} className="mt-1" />
            <div>
              <div className="label-tag text-destructive">
                {boleto.tipo === "PARCELA_ACORDO"
                  ? `Parcela ${boleto.acordo_parcela_numero}/${boleto.acordo_parcela_de} · Acordo`
                  : "Fatura em aberto"}
              </div>
              <div className="font-heading font-black text-3xl tracking-tighter mt-1">
                {fmtBRL(boleto.valor_total)}
              </div>
              <div className="text-xs mt-1">
                Competência {fmtCompetencia(boleto.competencia)} · Vencimento {fmtDate(boleto.vencimento)}
              </div>
            </div>
          </div>
          <div className="space-y-1 text-xs border-t border-destructive/20 pt-4 font-mono">
            <div className="flex justify-between"><span>Aluguel Base</span><span>{fmtBRL(boleto.valor_aluguel)}</span></div>
            {boleto.valor_iptu > 0 && (
              <div className="flex justify-between"><span>IPTU (cota mensal)</span><span>{fmtBRL(boleto.valor_iptu)}</span></div>
            )}
            {boleto.valor_agua > 0 && (
              <div className="flex justify-between"><span>Taxa de Água Comercial</span><span>{fmtBRL(boleto.valor_agua)}</span></div>
            )}
            <div className="flex justify-between"><span>Rateio de Despesas</span><span>{fmtBRL(boleto.valor_rateio)}</span></div>
            {(boleto.multa || 0) > 0 && (
              <div className="flex justify-between text-destructive"><span>Multa por Atraso</span><span>{fmtBRL(boleto.multa)}</span></div>
            )}
            {(boleto.juros || 0) > 0 && (
              <div className="flex justify-between text-destructive"><span>Mora / Juros</span><span>{fmtBRL(boleto.juros)}</span></div>
            )}
            {(boleto.residuos?.length ?? 0) > 0 && (
              <div data-testid="loj-residuos" className="border-t border-destructive/20 pt-2 mt-2 space-y-1">
                <div className="label-tag text-destructive">Ajustes / Resíduos</div>
                {boleto.residuos.map((r, idx) => (
                  <div key={r.id || idx} className={`flex justify-between ${r.valor >= 0 ? "text-destructive" : "text-success"}`}>
                    <span className="truncate mr-2">{r.descricao || "Ajuste"}</span>
                    <span>{r.valor >= 0 ? "+" : ""}{fmtBRL(r.valor)}</span>
                  </div>
                ))}
              </div>
            )}
            <div className="flex justify-between border-t border-destructive/20 pt-2 mt-2 font-bold">
              <span>TOTAL</span><span>{fmtBRL(boleto.valor_total)}</span>
            </div>
          </div>
          {boleto.tem_multiplas_unidades && boleto.unidades_breakdown?.length > 0 && (
            <div data-testid="loj-breakdown-unidades" className="mt-4 border-t border-destructive/20 pt-4">
              <div className="label-tag text-destructive mb-2">
                Discriminação por unidade ({boleto.unidades_breakdown.length} lojas)
              </div>
              <div className="space-y-3">
                {boleto.unidades_breakdown.map((u) => (
                  <div key={u.unidade_id} className="border border-destructive/20 rounded-xl p-3 bg-background/40">
                    <div className="flex items-center justify-between mb-2 flex-wrap gap-1">
                      <div>
                        <div className="font-semibold text-sm text-foreground">
                          {u.apelido || u.numero_completo}
                        </div>
                        {u.apelido && (
                          <div className="text-[10px] font-mono text-muted-foreground">{u.numero_completo}</div>
                        )}
                        <div className="text-[10px] font-mono text-muted-foreground">
                          {u.tipo} · {u.metragem}m²
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="label-tag">Subtotal</div>
                        <div className="stat-num text-lg text-foreground">{fmtBRL(u.subtotal)}</div>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[11px] font-mono text-foreground">
                      <div className="flex justify-between"><span>Aluguel</span><span>{fmtBRL(u.valor_aluguel)}</span></div>
                      <div className="flex justify-between"><span>Rateio</span><span>{fmtBRL(u.valor_rateio)}</span></div>
                      {u.valor_iptu > 0 && (
                        <div className="flex justify-between"><span>IPTU</span><span>{fmtBRL(u.valor_iptu)}</span></div>
                      )}
                      {u.valor_agua > 0 && (
                        <div className="flex justify-between"><span>Água</span><span>{fmtBRL(u.valor_agua)}</span></div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <div className="text-[10px] text-muted-foreground mt-2 leading-relaxed">
                Rateio dividido em cotas iguais por unidade. Aluguel e água proporcionais à metragem.
              </div>
            </div>
          )}
          <Button
            data-testid="loj-imprimir-boleto"
            onClick={async () => {
              try {
                const [benef, pix] = await Promise.all([
                  beneficiario ? Promise.resolve(beneficiario) : fetchBeneficiario(),
                  prepararPix(boleto.id, "lojista"),
                ]);
                if (benef && !beneficiario) setBeneficiario(benef);
                setComprovante(null);
                setPixReady(pix);
                setTimeout(() => printBoletoWithPageSize(), 200);
              } catch (e) {
                toast.error(extractError(e) || "Não foi possível gerar o Pix do Banco Inter para impressão");
              }
            }}
            variant="outline"
            className="w-full mt-4 rounded-xl h-11 no-print"
          >
            <Printer size={14} className="mr-2" /> Imprimir comprovante com QR Pix (PDF)
          </Button>
        </Card>
      ) : (
        <Card
          data-testid="loj-status-ok"
          className="p-6 rounded-2xl border-success/40 bg-success/5 text-success"
        >
          <div className="flex items-center gap-3">
            <CheckCircle2 size={28} />
            <div>
              <div className="label-tag text-success">Tudo em dia</div>
              <div className="font-heading font-bold text-xl mt-1">Nenhuma pendência</div>
              <div className="text-xs mt-1 opacity-80">Seu contrato está saudável 🟢</div>
            </div>
          </div>
        </Card>
      )}

      {/* Antecipar Pagamento — só quando não há boleto aberto/vencido */}
      {!temPendencia && (
        <Card
          data-testid="loj-antecipar-card"
          className="p-5 rounded-2xl border-2 border-warning/50 bg-warning/5"
        >
          <div className="flex items-start gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-warning/20 flex items-center justify-center flex-shrink-0">
              <Zap size={20} className="text-warning" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="label-tag text-warning">Adiantar aluguel</div>
              <div className="font-heading font-bold text-base leading-tight mt-1">
                Antecipar Pagamento
              </div>
              <div className="text-xs text-muted-foreground mt-1 leading-relaxed">
                Se você quiser adiantar, gere agora uma cobrança voluntária do <b>aluguel base</b> do mês corrente
                (rateio e IPTU entram apenas no fechamento oficial).
              </div>
            </div>
          </div>
          <Button
            data-testid="loj-antecipar-btn"
            onClick={antecipar}
            className="w-full h-12 rounded-xl bg-warning text-white hover:bg-warning/90 font-semibold"
          >
            <Zap size={16} className="mr-2" />
            Gerar Pix antecipado
          </Button>
        </Card>
      )}

      {/* Pagar botões — Pix (recomendado, sem taxa) + Cartão via Brick */}
      {temPendencia && (
        <div className="grid grid-cols-2 gap-3">
          <Button data-testid="loj-pagar-pix" onClick={() => iniciarCheckout("PIX")} disabled={loading} className="h-24 rounded-2xl flex-col gap-2 bg-foreground text-background hover:bg-foreground/90">
            <QrCode size={22} />
            <span className="text-xs font-semibold">Pagar com Pix</span>
            <span className="text-[10px] opacity-75 font-normal">QR na tela · sem taxa</span>
          </Button>
          <Button data-testid="loj-pagar-cartao" onClick={abrirCartao} disabled={loading} variant="outline" className="h-24 rounded-2xl flex-col gap-2 border-foreground/40">
            <CreditCard size={22} />
            <span className="text-xs font-semibold">Cartão de crédito</span>
            <span className="text-[10px] opacity-75 font-normal">Até 12x · seguro</span>
          </Button>
        </div>
      )}

      {/* Outros boletos em aberto — parcelas de acordo + mensalidade extra */}
      {outrosAbertos.length > 0 && (
        <Card className="p-5 rounded-2xl border-border" data-testid="loj-outros-abertos">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-heading font-bold text-base">Outros boletos em aberto</h2>
            <span className="label-tag">{outrosAbertos.length} pendente(s)</span>
          </div>
          <div className="space-y-2">
            {outrosAbertos.map((b) => {
              const isParcela = b.tipo === "PARCELA_ACORDO";
              const label = isParcela
                ? `Parcela ${b.acordo_parcela_numero}/${b.acordo_parcela_de} · Acordo`
                : `Mensalidade ${b.competencia}`;
              return (
                <button
                  key={b.id}
                  data-testid={`loj-outro-boleto-${b.id}`}
                  onClick={() => { setBoleto(b); window.scrollTo({top:0, behavior:'smooth'}); }}
                  className={`w-full text-left p-3 rounded-xl border transition-all hover:border-primary/50 flex items-center justify-between gap-3 ${isParcela ? "bg-blue-50 border-blue-200" : "bg-background border-border"}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-semibold text-foreground">{label}</div>
                    <div className="text-[10px] text-muted-foreground">
                      Vencimento {fmtDate(b.vencimento)}
                      {b.status === "VENCIDO" && <span className="text-destructive font-semibold"> · VENCIDO</span>}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-bold">{fmtBRL(b.valor_total)}</div>
                    <div className="text-[10px] text-primary">Pagar →</div>
                  </div>
                </button>
              );
            })}
          </div>
        </Card>
      )}

      {/* Histórico */}
      <Card className="p-5 rounded-2xl border-border">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading font-bold text-lg">Histórico</h2>
          <span className="label-tag">{historico.length} fatura(s)</span>
        </div>
        {historico.length === 0 ? (
          <div className="text-center py-6 text-muted-foreground text-sm">
            Sem histórico ainda.
          </div>
        ) : (
          <div className="space-y-2">
            {historico.slice(0, 8).map((h) => (
              <div key={h.id} data-testid={`loj-hist-${h.id}`} className="flex items-center justify-between border border-border/60 p-3 rounded-xl gap-2">
                <div className="min-w-0">
                  <div className="text-sm font-semibold">
                    {h.tipo === "PARCELA_ACORDO" ? `Parcela ${h.acordo_parcela_numero}/${h.acordo_parcela_de} · Acordo` : fmtCompetencia(h.competencia)}
                  </div>
                  <div className="text-xs text-muted-foreground font-mono">Venc {h.vencimento}</div>
                  {h.pago_em && (
                    <div className="text-[10px] font-mono text-success mt-0.5">
                      Pago em {fmtDateTime(h.pago_em)}
                    </div>
                  )}
                </div>
                <div className="text-right flex-shrink-0">
                  <div className="stat-num text-base">{fmtBRL(h.valor_total)}</div>
                  <Badge variant={h.status === "PAGO" ? "default" : "outline"} className={`text-[9px] uppercase tracking-widest mt-1 ${h.status === "PAGO" ? "bg-success text-white" : ""}`}>
                    {h.status}
                  </Badge>
                  {h.status === "PAGO" && (
                    <button type="button" data-testid={`loj-hist-comprovante-${h.id}`} onClick={() => imprimirComprovante(h)}
                      className="block ml-auto mt-1 text-[10px] text-primary underline underline-offset-2">
                      Comprovante
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Regimento Interno CTA */}
      <Link to="/lojista/regimento" data-testid="loj-cta-regimento" className="block">
        <Card className="p-5 rounded-2xl border-foreground/20 bg-foreground text-background hover:bg-foreground/90 transition-colors">
          <div className="flex items-center gap-3">
            <BookOpen size={22} />
            <div className="flex-1">
              <div className="font-heading font-bold">Regimento Interno</div>
              <div className="text-xs opacity-80">Leia as regras do VM Piabetá Shopping</div>
            </div>
            <span className="text-xs uppercase tracking-widest">Ver PDF →</span>
          </div>
        </Card>
      </Link>

      {/* Checkout modal */}
      <Dialog open={checkoutModal} onOpenChange={setCheckoutModal}>
        <DialogContent className="rounded-2xl max-w-sm" data-testid="loj-checkout-modal">
          <DialogHeader>
            <DialogTitle>{metodo === "PIX" ? "Pix · Banco Inter" : `Pagamento — ${metodo}`}</DialogTitle>
          </DialogHeader>
          {pagamento && metodo === "PIX" && (
            <div className="space-y-4">
              <div className="text-center">
                <div className="label-tag mb-2">Escaneie o QR code</div>
                {pagamento.pix_qr_code_base64 ? (
                  <img
                    src={`data:image/png;base64,${pagamento.pix_qr_code_base64}`}
                    alt="QR Code Pix"
                    className="w-40 h-40 mx-auto rounded-xl border border-border object-contain bg-white p-2"
                    data-testid="loj-pix-qr-img"
                  />
                ) : (
                  <div className="w-40 h-40 bg-muted mx-auto flex items-center justify-center rounded-xl border border-border">
                    <QrCode size={80} className="text-muted-foreground" />
                  </div>
                )}
              </div>
              <div>
                <div className="label-tag mb-2">Copia e cola</div>
                <div className="bg-secondary p-3 rounded-xl text-xs font-mono break-all border border-border">
                  {pagamento.pix_copia_cola}
                </div>
                <Button size="sm" variant="outline" onClick={() => copy(pagamento.pix_copia_cola)} className="w-full mt-2 rounded-xl" data-testid="loj-copiar-pix">
                  <Copy size={12} className="mr-2" /> Copiar código
                </Button>
              </div>
              <div className="stat-num text-center text-3xl" data-testid="loj-pix-valor">{fmtBRL(pagamento.valor)}</div>
              {pagamento.vencimento_cobranca && (
                <div className="text-center text-[11px] text-muted-foreground" data-testid="loj-pix-validade">
                  Válido para pagamento até {fmtDate(pagamento.vencimento_cobranca)}
                  {boleto?.status === "VENCIDO" && " · valor já com multa e juros do dia"}
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCheckoutModal(false)} className="w-full rounded-xl">
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de pagamento com cartão (Card Payment Brick) */}
      <Dialog open={cardModal} onOpenChange={setCardModal}>
        <DialogContent className="rounded-2xl max-w-md" data-testid="loj-card-modal">
          <DialogHeader>
            <DialogTitle>Pagar com cartão de crédito</DialogTitle>
          </DialogHeader>
          {cardResult ? (
            <div className="space-y-3 py-2">
              <div className={`p-4 rounded-xl text-center ${cardResult.status === "approved" ? "bg-success/10 text-success" : "bg-warning/10 text-warning"}`}>
                <div className="font-bold text-lg" data-testid="loj-card-status">
                  {cardResult.status === "approved" ? "Pagamento aprovado" : `Status: ${cardResult.status}`}
                </div>
                <div className="text-xs mt-1">
                  {cardResult.status_detail || "-"}
                </div>
                {cardResult.installments && (
                  <div className="text-xs mt-1 opacity-80">
                    {cardResult.installments}x · {fmtBRL(cardResult.valor)}
                  </div>
                )}
              </div>
              <Button onClick={() => setCardModal(false)} className="w-full rounded-xl">
                Fechar
              </Button>
            </div>
          ) : mpReady && boleto ? (
            <div className="py-2">
              <div className="text-center text-sm mb-3 text-muted-foreground">
                Total a pagar: <span className="font-bold text-foreground">{fmtBRL(boleto.valor_total)}</span>
              </div>
              <CardPayment
                initialization={{
                  amount: Number(boleto.valor_total),
                  payer: { email: lojista?.email || "" },
                }}
                customization={{
                  paymentMethods: {
                    types: { excluded: ["debit_card", "prepaid_card"] },
                    minInstallments: 1,
                    maxInstallments: 12,
                  },
                  visual: { style: { theme: "default" } },
                }}
                onSubmit={onCardSubmit}
                onError={(err) => {
                  console.error("[Card Brick]", err);
                  toast.error("Erro ao carregar o formulário de cartão");
                }}
              />
            </div>
          ) : (
            <div className="py-8 text-center text-sm text-muted-foreground">Carregando…</div>
          )}
        </DialogContent>
      </Dialog>

      {/* Layout de impressão — só aparece em @media print */}
      {lojista && comprovante && (
        <div className="print-only">
          <ComprovantePagamento fatura={comprovante} contrato={lojista} beneficiario={beneficiario} />
        </div>
      )}
      {temPendencia && lojista && !comprovante && pixReady && (
        <div className="print-only">
          <BoletoFebraban boleto={mesclarPix(boleto, pixReady)} contrato={lojista} beneficiario={beneficiario} />
        </div>
      )}
      <FloatingWhatsAppButton mensagemInicial={`Olá! Sou lojista do VM Piabetá Shopping (${lojista?.razao_social || ""}) e preciso de atendimento.`} />
    </div>
  );
}
